import { useEffect } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { type BaseFeatureFlags } from '../../launch-darkly.types';
import { useLaunchDarklyFactory } from '../useLaunchDarklyFactory';
import { type Params } from './useFeatureFlagFactory.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useFeatureFlagFactory = <
  Key extends keyof FeatureFlags,
  FeatureFlags extends BaseFeatureFlags,
>(
  key: Key,
  params: Params<FeatureFlags>,
): FeatureFlags[Key] => {
  const { Context, featureFlagOverrideKey, onChange } = params;

  const flags = useFlags<FeatureFlags>();
  const context = useLaunchDarklyFactory({ Context });

  const { flagOverrides, defaultFlags } = context;

  const value = flags[key];
  const defaultValue = defaultFlags[key];
  const overriddenValue = flagOverrides[key];

  const isOverrideSupported = featureFlagOverrideKey
    ? flags[featureFlagOverrideKey] === true
    : false;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    onChange?.(key, value);
  }, [key, value, onChange]);

  // ─────────────────────────────────────────────────────────────

  if (isOverrideSupported) {
    return overriddenValue ?? value ?? defaultValue;
  }

  return value ?? defaultValue;
};
